/**
 * El cronómetro que se pinta en pantalla.
 *
 * **Se repinta con `requestAnimationFrame`, no con un intervalo.** Un intervalo de un segundo hace
 * que el número salte de forma visible cuando la pestaña vuelve del segundo plano, y encima sigue
 * consumiendo con la pantalla apagada. El fotograma se detiene solo cuando el móvil se bloquea, que
 * es justo lo que se quiere.
 *
 * **Y solo se vuelve a renderizar cuando cambia el segundo entero**, no en cada fotograma: a
 * sesenta repintados por segundo, un Android de hace seis años dedica al cronómetro la batería que
 * necesita para responder.
 */

import { useEffect, useState } from 'react';
import { remainingAt, suspicionOfSuspension, type TimerAnchor } from '@clica/shared';
import { monoNow } from './use-game.js';

export interface ClockReading {
  /** Segundos enteros que quedan, o nulo si el reloj está parado. */
  readonly seconds: number | null;
  /** De cero a uno, para el anillo. Nulo si está parado. */
  readonly fraction: number | null;
  /** Cierto si el dispositivo parece haber estado suspendido y hay que esperar al servidor. */
  readonly stale: boolean;
}

const PARADO: ClockReading = { seconds: null, fraction: null, stale: false };

export function useClock(anchor: TimerAnchor | null): ClockReading {
  const [reading, setReading] = useState<ClockReading>(PARADO);

  useEffect(() => {
    if (anchor === null) {
      setReading(PARADO);
      return;
    }

    let frame = 0;
    let lastMono = monoNow();
    let lastWall = Date.now();
    let stale = false;

    const paint = (): void => {
      const mono = monoNow();
      const wall = Date.now();

      // La hora del sistema se usa **solo como delator**, nunca como fuente: si los dos relojes
      // discrepan, el móvil estuvo suspendido —o le han cambiado la hora— y lo que procede es
      // esperar al siguiente anclaje del servidor, no fiarse de ninguno de los dos.
      if (suspicionOfSuspension(mono - lastMono, wall - lastWall)) stale = true;
      lastMono = mono;
      lastWall = wall;

      const remaining = remainingAt(anchor, mono);
      if (remaining !== null) {
        const seconds = Math.ceil(remaining / 1000);
        const fraction = anchor.remainingMs === 0 ? 0 : remaining / anchor.remainingMs;

        setReading((previous) =>
          previous.seconds === seconds && previous.stale === stale
            ? previous
            : { seconds, fraction, stale },
        );
      }

      frame = requestAnimationFrame(paint);
    };

    frame = requestAnimationFrame(paint);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [anchor]);

  return reading;
}

/**
 * Los segundos que faltan para un instante que decide el servidor.
 *
 * Lo usa la cuenta atrás del arranque. **No se cuenta con una constante copiada al cliente**: eso
 * duplicaría en dos sitios cuánto dura la cuenta atrás, y el principio I dice que el tiempo lo
 * decide el servidor. Aquí solo se resta contra la marca que él manda.
 *
 * Se repinta una vez por segundo y no en cada fotograma: la cifra es entera, y a sesenta
 * repintados por segundo un Android viejo gasta en esto la batería que necesita para responder.
 */
export function useSegundosHasta(instante: string | null): number | null {
  const [segundos, setSegundos] = useState<number | null>(null);

  useEffect(() => {
    if (instante === null) {
      setSegundos(null);
      return;
    }

    const objetivo = Date.parse(instante);
    if (Number.isNaN(objetivo)) {
      setSegundos(null);
      return;
    }

    const medir = (): void => {
      const restan = Math.ceil((objetivo - Date.now()) / 1000);
      setSegundos(restan > 0 ? restan : null);
    };

    medir();
    const reloj = setInterval(medir, 250);
    return () => clearInterval(reloj);
  }, [instante]);

  return segundos;
}
